import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiService } from '../services/api.service';
import { FeatureFlagsStatus, FeatureKey, FeatureInfo } from '../types';

interface FeatureFlagsContextType {
  features: FeatureFlagsStatus;
  loading: boolean;
  isFeatureEnabled: (featureKey: FeatureKey) => boolean;
  isFeaturePremium: (featureKey: FeatureKey) => boolean;
  isFeatureAllowed: (featureKey: FeatureKey) => boolean;
  getFeature: (featureKey: FeatureKey) => FeatureInfo;
  refreshFeatures: () => Promise<void>;
  disabledNotification: { feature: string; message: string } | null;
  clearDisabledNotification: () => void;
}

const DEFAULT_FEATURE_INFO: FeatureInfo = {
  enabled: false,
  isPremium: false,
  requiredExtension: null,
  status: 'disabled',
};

const DEFAULT_FEATURE_FLAGS: FeatureFlagsStatus = {
  quota: { enabled: false, isPremium: false, requiredExtension: 'sharepoint', status: 'disabled' },
  entra_id_group_creation_lock: { enabled: false, isPremium: false, requiredExtension: 'directory', status: 'disabled' },
  sensitivity_labels: { enabled: false, isPremium: true, requiredExtension: 'sensitivity', status: 'disabled' },
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType>({
  features: DEFAULT_FEATURE_FLAGS,
  loading: false,
  isFeatureEnabled: () => false,
  isFeaturePremium: () => false,
  isFeatureAllowed: () => false,
  getFeature: () => DEFAULT_FEATURE_INFO,
  refreshFeatures: async () => {},
  disabledNotification: null,
  clearDisabledNotification: () => {},
});

export const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [features, setFeatures] = useState<FeatureFlagsStatus>(DEFAULT_FEATURE_FLAGS);
  const [loading, setLoading] = useState<boolean>(true);
  const [disabledNotification, setDisabledNotification] = useState<{ feature: string; message: string } | null>(null);

  const refreshFeatures = useCallback(async () => {
    try {
      const response = await apiService.getFeatureFlagsStatus();
      if (response.success && response.data) {
        setFeatures(prev => ({
          ...prev,
          ...response.data,
        }));
      }
    } catch (err) {
      console.warn('[FeatureFlagsContext] Error refreshing features (fail-open applied):', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshFeatures();

    // 🚩 Écoute des rejets 403 FEATURE_DISABLED provenant d'apiService
    const unsubscribe = apiService.onFeatureDisabled((feature, message) => {
      setDisabledNotification({ feature, message });
      // Invalidation / rechargement automatique des features à chaud
      refreshFeatures();
    });

    return () => {
      unsubscribe();
    };
  }, [refreshFeatures]);

  const getFeature = useCallback((featureKey: FeatureKey): FeatureInfo => {
    return features[featureKey] ?? DEFAULT_FEATURE_INFO;
  }, [features]);

  const isFeatureEnabled = useCallback((featureKey: FeatureKey): boolean => {
    const feature = features[featureKey];
    if (!feature) return false;
    // Gère à la fois le format objet { enabled, isPremium } et l'éventuel booléen brut
    return typeof feature === 'boolean' ? feature : (feature.enabled ?? false);
  }, [features]);

  const isFeaturePremium = useCallback((featureKey: FeatureKey): boolean => {
    const feature = features[featureKey];
    if (!feature) return false;
    return typeof feature === 'boolean' ? false : (feature.isPremium ?? false);
  }, [features]);

  /**
   * Vérifie si la fonctionnalité est utilisable côté tenant :
   * - Feature active (enabled: true)
   * - Si isPremium, statut ne doit pas être 'upgrade_required'
   */
  const isFeatureAllowed = useCallback((featureKey: FeatureKey): boolean => {
    const feature = features[featureKey];
    if (!feature) return false;
    const enabled = typeof feature === 'boolean' ? feature : (feature.enabled ?? false);
    if (!enabled) return false;
    if (typeof feature === 'object') {
      if (feature.status === 'disabled') return false;
      if (feature.status === 'upgrade_required') return false;
    }
    return true;
  }, [features]);

  const clearDisabledNotification = useCallback(() => {
    setDisabledNotification(null);
  }, []);

  return (
    <FeatureFlagsContext.Provider
      value={{
        features,
        loading,
        isFeatureEnabled,
        isFeaturePremium,
        isFeatureAllowed,
        getFeature,
        refreshFeatures,
        disabledNotification,
        clearDisabledNotification,
      }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook d'accès aux Feature Flags
 */
export const useFeatureFlags = () => useContext(FeatureFlagsContext);

/**
 * Hook spécifique pour vérifier si une fonctionnalité donnée est active (visibilité système)
 * @param featureKey Clé de la fonctionnalité (ex: 'quota', 'entra_id_group_creation_lock')
 */
export const useFeature = (featureKey: FeatureKey): boolean => {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled(featureKey);
};

/**
 * Hook spécifique pour vérifier si une fonctionnalité est utilisable pour le tenant (active ET accessible au plan courant)
 * Si la feature est désactivée OU si elle est payante et le tenant en free, renvoie false.
 */
export const useFeatureAllowed = (featureKey: FeatureKey): boolean => {
  const { isFeatureAllowed } = useFeatureFlags();
  return isFeatureAllowed(featureKey);
};

/**
 * Hook spécifique pour obtenir toutes les infos d'une fonctionnalité ({ enabled, isPremium, status, etc. })
 * @param featureKey Clé de la fonctionnalité (ex: 'quota', 'entra_id_group_creation_lock')
 */
export const useFeatureInfo = (featureKey: FeatureKey): FeatureInfo => {
  const { getFeature } = useFeatureFlags();
  return getFeature(featureKey);
};
